import { getDefaultImage } from '@/assets/defaults';
import { Link } from 'react-router-dom';

interface PublicationCardProps {
  id: string;
  title: string;
  image?: string | null;
  category?: string;
  collection: string;
  createdAt?: { seconds: number } | null;
  detailPath: string;
}

export function formatRelativeDate(seconds: number): string {
  const now = new Date();
  const date = new Date(seconds * 1000);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(date, now)) return 'Hoy';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Ayer';

  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((nowMidnight.getTime() - dateMidnight.getTime()) / 86400000);
  if (days === 1) return 'Hace 1 día';
  if (days < 30) return `Hace ${days} días`;
  const months = Math.floor(days / 30);
  if (months === 1) return 'Hace 1 mes';
  if (months < 12) return `Hace ${months} meses`;
  return `Hace ${Math.floor(months / 12)} año(s)`;
}

const PublicationCard: React.FC<PublicationCardProps> = ({
  title,
  image,
  category,
  collection,
  createdAt,
  detailPath,
}) => {
  const defaultSrc = getDefaultImage(collection, category);
  const imgSrc = image || defaultSrc;

  return (
    <Link
      to={detailPath}
      className="block bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="h-36 overflow-hidden bg-gray-100">
        <img
          src={imgSrc}
          alt={title}
          className="w-full h-full object-cover"
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            if (img.src !== defaultSrc) img.src = defaultSrc;
          }}
        />
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-gray-800 line-clamp-2">{title}</p>
        <p data-testid="pub-date" className="text-xs text-gray-500 mt-1">
          {createdAt?.seconds ? formatRelativeDate(createdAt.seconds) : '—'}
        </p>
      </div>
    </Link>
  );
};

export default PublicationCard;
